import { useEffect, useState, useCallback, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { fmtError } from "@/lib/api";
import { fmtINR, fmtDate } from "@/lib/format";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import {
  FileText, Plus, Search, Building2, User, Package, Trash2, Download,
  MessageSquare, Mail, CheckCircle2, XCircle, Clock, RotateCcw, ChevronRight, ChevronLeft, ArrowRight, Pencil,
} from "lucide-react";
import WhatsAppButton from "@/components/WhatsAppButton";
import CallButton from "@/components/CallButton";

export const STATUS_LABEL = {
  draft: "Draft",
  sent: "Sent",
  accepted: "Accepted",
  rejected: "Rejected",
  expired: "Expired",
};

export const STATUS_COLOR = {
  draft: "bg-zinc-100 text-zinc-700 border-zinc-200",
  sent: "bg-amber-50 text-amber-800 border-amber-200",
  accepted: "bg-primary-50 text-primary-700 border-primary-200",
  rejected: "bg-red-50 text-red-700 border-red-200",
  expired: "bg-stone-100 text-stone-500 border-stone-200",
};

// GST is hard-coded company-wide at 18% (CGST 9% + SGST 9%) — every line on
// every quote is taxed at this rate regardless of state. Per-line rate
// overrides are intentionally NOT exposed in the wizard.
const UNIVERSAL_GST_PCT = 18;

function emptyItem() {
  // `_key` is a stable client-side React key for the items array since the
  // backend snapshots items into a quotation only after submit (so we can't
  // use a server id for the in-progress list). Stripped from the payload
  // before submit() so it doesn't leak to the API.
  return {
    _key: (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `it-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    name: "", description: "", hsn: "", quantity: 1, unit: "Nos", unit_price: 0, discount_pct: 0, gst_pct: UNIVERSAL_GST_PCT, product_id: null, accessories: [],
  };
}

function calcLine(it) {
  const qty = Number(it.quantity) || 0;
  const up = Number(it.unit_price) || 0;
  const disc = Number(it.discount_pct) || 0;
  const gross = qty * up;
  const discAmt = gross * disc / 100;
  const net = gross - discAmt;
  return { gross, discAmt, net };
}

// Inline catalog search for item rows — typing shows matching Products /
// Services / Trainings (name or code); picking one auto-fills the row while
// keeping every field manually editable (corrections doc: "+ Blank row"
// must offer the same pick-from-catalog UX as the first row).
function CatalogNameInput({ idx, value, onChange, onPick, options }) {
  const [focused, setFocused] = useState(false);
  const q = (value || "").trim().toLowerCase();
  const matches = focused && q.length >= 2
    ? options.filter((o) =>
        (o.name || "").toLowerCase().includes(q) || (o.sku || "").toLowerCase().includes(q)
      ).slice(0, 8)
    : [];
  return (
    <div className="relative">
      <Input
        className="rounded-xl h-9 text-sm font-semibold"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setTimeout(() => setFocused(false), 150)}
        placeholder="Type to search catalog, or enter item name *"
        data-testid={`quotation-item-name-${idx}`}
      />
      {matches.length > 0 && (
        <div className="absolute z-50 left-0 right-0 mt-1 rounded-2xl border border-border bg-card shadow-lg max-h-56 overflow-y-auto" data-testid={`quotation-item-search-results-${idx}`}>
          {matches.map((o) => (
            <button
              type="button"
              key={`${o._kind}-${o.id}`}
              onMouseDown={(e) => { e.preventDefault(); onPick(o); setFocused(false); }}
              className="w-full text-left px-3 py-2 text-xs hover:bg-muted/60 border-b border-border/40 last:border-b-0"
              data-testid={`quotation-item-search-pick-${o.id}`}
            >
              <span className="font-semibold">{o.name}</span>
              <span className="text-muted-foreground">
                {o.sku ? ` · ${o.sku}` : ""}{o.unit_price ? ` · ${fmtINR(o.unit_price)}` : ""}
                {" · "}{o._kind === "product" ? "Product" : o._kind === "training" ? "Training" : "Service"}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function NewQuotationWizard({ open, onClose, onCreated, prefilledClientId, prefilledDealId, prefilledProductId, editQuote }) {
  const editingId = editQuote?.id || null;
  const [step, setStep] = useState(1);
  const [clients, setClients] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [products, setProducts] = useState([]);
  const [services, setServices] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [deals, setDeals] = useState([]);
  const [company, setCompany] = useState(null);
  const [saving, setSaving] = useState(false);
  // Which catalogue the "Add from catalog" picker on step 2 reads from.
  // Defaults to the quote category, but the user can switch to mix
  // products + services + trainings in the same quotation.
  const [pickerKind, setPickerKind] = useState(null);
  // Per-company Product Type filter used by the step-2 catalog pickers.
  const [productTypes, setProductTypes] = useState([]);
  const [typeFilter, setTypeFilter] = useState("");
  // Manual Authorised-Signatory selection — ADMIN ONLY (15.docx); everyone
  // else automatically signs with their own uploaded signature.
  const { user: authUser } = useAuth();
  const isAdmin = authUser?.role === "admin";
  const [signatories, setSignatories] = useState([]);

  const [form, setForm] = useState({
    client_id: prefilledClientId || "",
    contact_id: "",
    deal_id: prefilledDealId || "",
    subject: "",
    quote_type: "final",
    service_category: "products",   // products | services | training — drives T&C template
    service_subtype: "general",     // bearing_lubrication | leak_detection | electrical_pd | general
    manufacturer_id: "",            // filters catalogue items by Company (UE Systems / FOTRIC / …)
    items: [emptyItem()],
    scope: "",
    prerequisites: "",
    terms: "",
    notes: "",
    interstate: false,
    validity_days: 30,
    valid_until: "",
    // Salesperson nudges this on the Review step to land on the final
    // negotiated number — applied AFTER per-line discounts, BEFORE GST.
    overall_discount_pct: 0,
    signatory_user_id: "",
  });

  useEffect(() => {
    if (!open) return;
    if (isAdmin) api.get("/quotation-signatories").then((r) => setSignatories(r.data || [])).catch(() => setSignatories([]));
    Promise.all([
      api.get("/clients"),
      api.get("/products"),
      api.get("/services"),
      api.get("/deals"),
      api.get("/quotations/company"),
      api.get("/manufacturers").catch(() => ({ data: [] })),
      api.get("/product-types").catch(() => ({ data: [] })),
    ]).then(([c, p, sv, d, co, mf, pt]) => {
      setClients(c.data || []);
      setProducts(p.data || []);
      setServices(sv.data || []);
      setDeals(d.data || []);
      setCompany(co.data || null);
      setManufacturers((mf.data || []).filter((m) => m.is_active !== false));
      setProductTypes((pt.data || []).filter((t) => t.is_active !== false));
      if (editQuote) {
        // Edit mode — pre-fill the wizard with the existing quote so the
        // user can tweak any field and re-download the PDF without burning
        // a fresh quote number. Quote type stays locked (committed at create).
        setForm({
          client_id: editQuote.client_id || "",
          contact_id: editQuote.contact_id || "",
          deal_id: editQuote.deal_id || "",
          subject: editQuote.subject || "",
          quote_type: editQuote.quote_type || "final",
          service_category: (editQuote.service_category === "sales" ? "products" : editQuote.service_category) || "services",
          service_subtype: ({ bearing: "bearing_lubrication", leaks: "leak_detection", electrical: "electrical_pd" }[editQuote.service_subtype] || editQuote.service_subtype || "general"),
          items: (editQuote.items || []).map((it) => ({
            ...emptyItem(), ...it,
            // `_key` from emptyItem() is preserved as fallback; if the saved
            // item already has its own _key (re-edited quote) we keep that.
            _key: it._key || ((typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `it-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`),
            unit_price: Number(it.unit_price) || 0,
            quantity: Number(it.quantity) || 1,
            discount_pct: Number(it.discount_pct) || 0,
          })),
          scope: editQuote.scope || "",
          prerequisites: editQuote.prerequisites || "",
          terms: editQuote.terms || "",
          notes: editQuote.notes || "",
          interstate: !!editQuote.interstate,
          validity_days: editQuote.validity_days || co.data?.default_validity_days || 30,
          valid_until: editQuote.valid_until ? editQuote.valid_until.slice(0, 10) : "",
          overall_discount_pct: Number(editQuote.overall_discount_pct) || 0,
          signatory_user_id: editQuote.signatory_user_id || "",
        });
      } else {
        // FRESH quotation — always reset to a blank Step-1 form so data
        // from a previously created quote never leaks into the next one.
        setStep(1);
        setPickerKind(null);
        setTypeFilter("");
        setContacts([]);
        setForm({
          client_id: prefilledClientId || "",
          contact_id: "",
          deal_id: prefilledDealId || "",
          subject: "",
          quote_type: "final",
          service_category: "products",
          service_subtype: "general",
          manufacturer_id: "",
          items: [emptyItem()],
          scope: "",
          prerequisites: "",
          terms: co.data?.default_terms || "",
          notes: "",
          interstate: false,
          validity_days: co.data?.default_validity_days || 30,
          valid_until: "",
          overall_discount_pct: 0,
        });
        // Deep-link from a product detail page ("Request quotation") —
        // preload the product as the first line item + its company.
        if (prefilledProductId) {
          const pre = (p.data || []).find((x) => x.id === prefilledProductId);
          if (pre) {
            const companyName = (mf.data || []).find((m) => m.id === pre.manufacturer_id)?.name || null;
            setForm((f) => ({
              ...f,
              service_category: "products",
              manufacturer_id: pre.manufacturer_id || "",
              items: [{
                ...emptyItem(),
                name: pre.name,
                description: pre.short_description || pre.description || "",
                hsn: pre.hsn || "",
                unit: pre.uom || "Nos",
                unit_price: pre.unit_price || 0,
                product_id: pre.id,
                item_kind: "product",
                sku: pre.sku || "",
                company_name: companyName,
                accessories: Array.isArray(pre.accessories) ? [...pre.accessories] : [],
              }],
            }));
          }
        }
      }
    });
  }, [open, editQuote, prefilledProductId]);

  useEffect(() => {
    if (!form.client_id) { setContacts([]); return; }
    api.get(`/clients/${form.client_id}`).then((r) => {
      setContacts(r.data?.contacts || []);
      // Auto-pick the first (primary) contact so the Contact Person is
      // automatically available as soon as the client is chosen.
      if (r.data?.contacts?.length) {
        const primary = r.data.contacts[0];
        setForm((f) => f.contact_id ? f : { ...f, contact_id: primary.id });
      }
    }).catch(() => setContacts([]));
  }, [form.client_id]);

  // Auto-fill Scope of Work + Client Prerequisites from the verbatim
  // master-document templates whenever the rep changes service subtype.
  // Only overwrites the textareas when they're empty or still match a
  // previously auto-filled template — manual edits (typed text that
  // doesn't match any template) are preserved. For category=products we
  // clear them since on-site scope/prereq don't apply to product sales.
  useEffect(() => {
    if (!company || !open || editingId) return;
    if (form.service_category === "products") {
      setForm((f) => ({ ...f, scope: "", prerequisites: "" }));
      return;
    }
    const sub = form.service_subtype || "general";
    const newScope = company[`default_scope_${sub}`] || "";
    const newPrereq = company[`default_prereq_${sub}`] || "";
    setForm((f) => {
      // Detect whether existing textbox value matches any subtype template
      // so we can safely swap it. Manually-edited content stays intact.
      const subtypes = ["bearing_lubrication", "leak_detection", "electrical_pd", "general"];
      const isAutoScope = !f.scope || subtypes.some((s) => company[`default_scope_${s}`] && f.scope === company[`default_scope_${s}`]);
      const isAutoPrereq = !f.prerequisites || subtypes.some((s) => company[`default_prereq_${s}`] && f.prerequisites === company[`default_prereq_${s}`]);
      return {
        ...f,
        scope: isAutoScope ? newScope : f.scope,
        prerequisites: isAutoPrereq ? newPrereq : f.prerequisites,
      };
    });
  }, [form.service_subtype, form.service_category, company, open, editingId]);

  const addProduct = (productId) => {
    const p = products.find((x) => x.id === productId);
    if (!p) return;
    const companyName = manufacturers.find((m) => m.id === p.manufacturer_id)?.name || null;
    setForm((f) => ({
      ...f,
      items: [
        ...f.items.filter((it) => it.name || it.unit_price),
        {
          ...emptyItem(),
          name: p.name,
          description: p.short_description || p.description || "",
          hsn: p.hsn || "",
          unit: p.uom || "Nos",
          unit_price: p.unit_price || 0,
          gst_pct: p.gst_pct ?? 18,
          product_id: p.id,
          item_kind: "product",
          // Historical snapshot — code + company pinned at add-time so
          // future master edits never change this quotation.
          sku: p.sku || "",
          company_name: companyName,
          // Snapshot the product's bundled accessories onto the line so the
          // PDF can render an "Includes:" block per item.
          accessories: Array.isArray(p.accessories) ? [...p.accessories] : [],
        },
      ],
    }));
  };

  // Add a technical-service or training line. Pulls from the `services`
  // catalogue; `kind` distinguishes trainings for downstream renderers.
  const addService = (serviceId, kind = "service") => {
    const s = services.find((x) => x.id === serviceId);
    if (!s) return;
    const companyName = manufacturers.find((m) => m.id === s.manufacturer_id)?.name || null;
    setForm((f) => ({
      ...f,
      items: [
        ...f.items.filter((it) => it.name || it.unit_price),
        {
          ...emptyItem(),
          name: s.name,
          description: s.description || "",
          // Services use SAC codes — leave `hsn` blank so the PDF column
          // shows the SAC code instead. Stored separately on the line.
          hsn: s.sac_code || "",
          unit: s.uom || (kind === "training" ? "Course" : "Service"),
          unit_price: s.unit_price || 0,
          gst_pct: 18,
          service_id: s.id,
          item_kind: kind,
          sku: s.sku || "",
          company_name: companyName,
        },
      ],
    }));
  };

  const updateItem = (idx, patch) => {
    setForm((f) => ({ ...f, items: f.items.map((it, i) => (i === idx ? { ...it, ...patch } : it)) }));
  };

  const removeItem = (idx) => {
    setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));
  };

  // Fill an EXISTING row in place from a catalog entry — used by the per-row
  // search dropdown so "+ Blank row" rows get the same pick-from-catalog UX
  // as the first row (corrections doc). Manual edits stay possible after.
  const fillItemFromCatalog = (idx, o) => {
    const companyName = manufacturers.find((m) => m.id === o.manufacturer_id)?.name || null;
    const patch = o._kind === "product" ? {
      name: o.name,
      description: o.short_description || o.description || "",
      hsn: o.hsn || "",
      unit: o.uom || "Nos",
      unit_price: o.unit_price || 0,
      gst_pct: o.gst_pct ?? 18,
      product_id: o.id,
      service_id: null,
      item_kind: "product",
      sku: o.sku || "",
      company_name: companyName,
      accessories: Array.isArray(o.accessories) ? [...o.accessories] : [],
    } : {
      name: o.name,
      description: o.description || "",
      hsn: o.sac_code || "",
      unit: o.uom || (o._kind === "training" ? "Course" : "Service"),
      unit_price: o.unit_price || 0,
      gst_pct: 18,
      service_id: o.id,
      product_id: null,
      item_kind: o._kind,
      sku: o.sku || "",
      company_name: companyName,
    };
    updateItem(idx, patch);
  };

  const catalogOptions = useMemo(() => ([
    ...products.filter((p) => p.is_active !== false && p.show_in_quotation !== false).map((p) => ({ ...p, _kind: "product" })),
    ...services.filter((s) => s.is_active !== false).map((s) => ({ ...s, _kind: s.is_training ? "training" : "service" })),
  ]), [products, services]);

  const totals = useMemo(() => {
    let bagNet = 0, lineDiscount = 0;
    form.items.forEach((it) => {
      const c = calcLine(it);
      bagNet += c.net;
      lineDiscount += c.discAmt;
    });
    const overallPct = Math.max(0, Math.min(100, Number(form.overall_discount_pct) || 0));
    const overallDiscount = bagNet * overallPct / 100;
    const taxable = bagNet - overallDiscount;
    const gst = taxable * UNIVERSAL_GST_PCT / 100;
    return {
      subtotal: bagNet,
      lineDiscount,
      overallDiscount,
      overallPct,
      taxable,
      gst,
      grand: taxable + gst,
    };
  }, [form.items, form.overall_discount_pct]);

  const submit = async () => {
    const validItems = form.items.filter((it) => (it.name || "").trim());
    if (!validItems.length) { toast.error("Add at least one item"); return; }
    if (!form.client_id) { toast.error("Pick a client"); return; }
    setSaving(true);
    try {
      const { _discount_mode: _dm, ...formClean } = form;
      const payload = {
        ...formClean,
        signatory_user_id: form.signatory_user_id || null,
        valid_until: form.valid_until || null,
        overall_discount_pct: Number(form.overall_discount_pct) || 0,
        items: validItems.map((it) => ({
          name: it.name,
          description: it.description || null,
          hsn: it.hsn || null,
          quantity: Number(it.quantity) || 0,
          unit: it.unit || "Nos",
          unit_price: Number(it.unit_price) || 0,
          discount_pct: Number(it.discount_pct) || 0,
          gst_pct: UNIVERSAL_GST_PCT,
          product_id: it.product_id || null,
          service_id: it.service_id || null,
          item_kind: it.item_kind || (it.service_id ? "service" : "product"),
          sku: it.sku || null,
          company_name: it.company_name || null,
          accessories: (it.accessories || []).filter((a) => a && String(a).trim()),
        })),
      };
      const { data } = editingId
        ? await api.patch(`/quotations/${editingId}`, payload)
        : await api.post("/quotations", payload);
      toast.success(editingId ? `Quotation ${data.number} updated` : `Quotation ${data.number} created`);
      onCreated?.(data);
      onClose();
    } catch (err) { toast.error(fmtError(err)); } finally { setSaving(false); }
  };

  if (!open) return null;

  const selectedClient = clients.find((c) => c.id === form.client_id);
  const selectedContact = contacts.find((c) => c.id === form.contact_id);
  const canStep2 = !!form.client_id;
  const canStep3 = canStep2 && form.items.some((it) => (it.name || "").trim());
  const canStep4 = canStep3;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-end sm:place-items-center p-0 sm:p-6 overflow-y-auto" data-testid="quotation-wizard">
      <Card className="rounded-t-3xl sm:rounded-3xl border-border/60 shadow-soft w-full max-w-4xl max-h-[95vh] flex flex-col">
        <CardContent className="p-5 sm:p-7 flex flex-col flex-1 overflow-hidden">
          <div className="flex items-center justify-between gap-2 mb-5 shrink-0">
            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">
                {editingId ? `Editing ${editQuote?.number || ""}` : `Step ${step} of 4`}
              </p>
              <h2 className="font-display text-2xl font-extrabold tracking-tight mt-0.5">
                {step === 1 && "Customer"}
                {step === 2 && "Products & pricing"}
                {step === 3 && "Terms & conditions"}
                {step === 4 && "Review & confirm"}
              </h2>
            </div>
            <Button variant="ghost" onClick={onClose} className="rounded-2xl h-9 w-9 p-0" data-testid="quotation-close">
              ✕
            </Button>
          </div>

          {/* Progress bar */}
          <div className="flex items-center gap-1.5 mb-5 shrink-0">
            {[1, 2, 3, 4].map((s) => (
              <div key={s} className={`h-1.5 flex-1 rounded-full transition ${step >= s ? "bg-primary" : "bg-muted"}`} />
            ))}
          </div>

          <div className="overflow-y-auto flex-1 -mx-1 px-1">
            {step === 1 && (
              <div className="space-y-4">
                {/* Quote type — only allow this on create. After save the
                    type is locked because the number is committed. Demo &
                    Budgetary get a watermark on the PDF and a separate
                    counter so they don't burn final-series numbers. */}
                {!editingId && (
                  <div>
                    <Label className="text-xs font-semibold">Quote type</Label>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-1.5" data-testid="quotation-type">
                      {[
                        { v: "budgetary",        l: "Budgetary",        d: "Indicative · early-stage planning" },
                        { v: "purchase_enquiry", l: "Purchase Enquiry", d: "Customer responding to RFQ" },
                        { v: "negotiation",      l: "Negotiation",      d: "Revised pricing in discussion" },
                        { v: "final",            l: "Final",            d: "Committed price · sent to customer" },
                      ].map((opt) => (
                        <button
                          type="button"
                          key={opt.v}
                          onClick={() => setForm({ ...form, quote_type: opt.v })}
                          data-testid={`quotation-type-${opt.v}`}
                          className={`p-3 rounded-2xl border text-left tap transition ${
                            form.quote_type === opt.v
                              ? "border-primary bg-primary-50 ring-2 ring-primary/30"
                              : "border-border/60 hover:bg-muted/30"
                          }`}
                        >
                          <p className="font-semibold text-sm">{opt.l}</p>
                          <p className="text-[10px] text-muted-foreground leading-snug mt-0.5">{opt.d}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {/* Category picker — drives which T&C set auto-fills AND
                    which catalogue table the item picker reads from.
                    Products  → products table  (Ultraprobe etc.)
                    Services  → services table  (is_training != true)
                    Training  → services table  (is_training == true) */}
                <div>
                  <Label className="text-xs font-semibold">Category</Label>
                  <div className="grid grid-cols-3 gap-2 mt-1.5" data-testid="quotation-category">
                    {[
                      { v: "products", l: "Products", d: "Product sale · delivery + warranty" },
                      { v: "services", l: "Services", d: "On-site analysis · express + idle-time" },
                      { v: "training", l: "Training", d: "Certification / course · schedule + prereqs" },
                    ].map((opt) => (
                      <button
                        type="button"
                        key={opt.v}
                        onClick={() => setForm({ ...form, service_category: opt.v, manufacturer_id: "" })}
                        data-testid={`quotation-category-${opt.v}`}
                        className={`p-3 rounded-2xl border text-left tap transition ${
                          form.service_category === opt.v
                            ? "border-primary bg-primary-50 ring-2 ring-primary/30"
                            : "border-border/60 hover:bg-muted/30"
                        }`}
                      >
                        <p className="font-semibold text-sm">{opt.l}</p>
                        <p className="text-[10px] text-muted-foreground leading-snug mt-0.5">{opt.d}</p>
                      </button>
                    ))}
                  </div>
                </div>
                {/* Company selector — pick the company FIRST; the step-2
                    picker then filters items (and Product Types) by it.
                    Shown for every category per the corrections doc. */}
                <div>
                  <Label className="text-xs font-semibold">Select Company</Label>
                  <Select
                    value={form.manufacturer_id || "__all"}
                    onValueChange={(v) => { setForm({ ...form, manufacturer_id: v === "__all" ? "" : v }); setTypeFilter(""); }}
                  >
                    <SelectTrigger className="rounded-2xl h-11 mt-1.5" data-testid="quotation-manufacturer-select">
                      <SelectValue placeholder="Any" />
                    </SelectTrigger>
                    <SelectContent className="rounded-2xl">
                      <SelectItem value="__all">All companies</SelectItem>
                      {manufacturers.map((m) => (
                        <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-muted-foreground mt-1">
                    The item picker on the next step only shows this company's items.
                  </p>
                </div>
                {/* Service subtype picker — only relevant for Services category.
                    Selects which prerequisite checklist auto-fills on the PDF. */}
                {form.service_category === "services" && (
                  <div>
                    <Label className="text-xs font-semibold">Service type (drives prerequisites)</Label>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-1.5" data-testid="quotation-subtype">
                      {[
                        { v: "bearing_lubrication", l: "Bearing & Lubrication" },
                        { v: "leak_detection",      l: "Leak Detection" },
                        { v: "electrical_pd",       l: "Electrical PD Inspection" },
                        { v: "general",             l: "General / other" },
                      ].map((opt) => (
                        <button
                          type="button"
                          key={opt.v}
                          onClick={() => setForm({ ...form, service_subtype: opt.v })}
                          data-testid={`quotation-subtype-${opt.v}`}
                          className={`p-2.5 rounded-2xl border text-left tap transition ${
                            form.service_subtype === opt.v
                              ? "border-primary bg-primary-50 ring-2 ring-primary/30"
                              : "border-border/60 hover:bg-muted/30"
                          }`}
                        >
                          <p className="font-semibold text-xs">{opt.l}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <div>
                  <Label className="text-xs font-semibold">Client *</Label>
                  <Select value={form.client_id || "__none"} onValueChange={(v) => {
                    const cid = v === "__none" ? "" : v;
                    setForm((f) => {
                      const d = deals.find((x) => x.id === f.deal_id);
                      // Reset the linked deal if it belongs to another client.
                      return { ...f, client_id: cid, contact_id: "", deal_id: d && cid && d.client_id !== cid ? "" : f.deal_id };
                    });
                  }}>
                    <SelectTrigger className="rounded-2xl h-11 mt-1.5" data-testid="quotation-client-select"><SelectValue placeholder="Pick a client" /></SelectTrigger>
                    <SelectContent className="rounded-2xl">
                      <SelectItem value="__none">— Select —</SelectItem>
                      {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {selectedClient && (
                    <div className="mt-2 p-3 rounded-2xl bg-muted/40 text-xs space-y-0.5">
                      <p>{selectedClient.location || "—"}</p>
                      {selectedClient.gst_number && <p>GSTIN: <strong>{selectedClient.gst_number}</strong></p>}
                      {selectedClient.email && <p>{selectedClient.email}</p>}
                    </div>
                  )}
                </div>
                {form.client_id && (
                  <div>
                    <Label className="text-xs font-semibold">Contact (decision-maker)</Label>
                    <Select value={form.contact_id || "__none"} onValueChange={(v) => setForm({ ...form, contact_id: v === "__none" ? "" : v })}>
                      <SelectTrigger className="rounded-2xl h-11 mt-1.5" data-testid="quotation-contact-select"><SelectValue placeholder={contacts.length ? "Pick a contact" : "No contacts on file"} /></SelectTrigger>
                      <SelectContent className="rounded-2xl">
                        <SelectItem value="__none">— Use client's primary contact —</SelectItem>
                        {contacts.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}{c.designation ? `, ${c.designation}` : ""}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div>
                  <Label className="text-xs font-semibold">Subject</Label>
                  <Input className="rounded-2xl h-11 mt-1.5" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="e.g. Vibration diagnostics package — kiln drives" data-testid="quotation-subject" />
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-4">
                {/* Mixed-item picker — a quotation can contain products,
                    services AND trainings together. Every tab gets the same
                    Company → Product Type → Item cascade so reps only see
                    relevant items. */}
                {(() => {
                  const activePickKind = pickerKind || (form.service_category === "products" ? "products" : form.service_category);
                  const mid = form.manufacturer_id || null;
                  const typesForCompany = productTypes.filter((t) => !mid || t.manufacturer_id === mid);
                  const tid = typeFilter && typesForCompany.some((t) => t.id === typeFilter) ? typeFilter : "";
                  const filteredProducts = products
                    .filter((p) => p.is_active !== false && p.show_in_quotation !== false)
                    .filter((p) => !mid || p.manufacturer_id === mid)
                    .filter((p) => !tid || p.product_type_id === tid)
                    .sort((a, b) => (a.display_order || 0) - (b.display_order || 0) || (a.name || "").localeCompare(b.name || ""));
                  const filteredServices = services
                    .filter((s) => !s.is_training && s.is_active !== false)
                    .filter((s) => !mid || s.manufacturer_id === mid)
                    .filter((s) => !tid || s.product_type_id === tid);
                  const filteredTrainings = services
                    .filter((s) => s.is_training && s.is_active !== false)
                    .filter((s) => !mid || s.manufacturer_id === mid)
                    .filter((s) => !tid || s.product_type_id === tid);
                  const oemLabel = mid ? (manufacturers.find((m) => m.id === mid)?.name || "") : "";
                return (
                <div className="space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  <Label className="text-xs font-semibold">Add from catalog</Label>
                  <div className="inline-flex rounded-2xl border border-border/60 overflow-hidden bg-card" data-testid="quotation-picker-kind">
                    {[
                      { v: "products", l: "Products" },
                      { v: "services", l: "Services" },
                      { v: "training", l: "Training" },
                    ].map((opt) => (
                      <button
                        type="button"
                        key={opt.v}
                        onClick={() => setPickerKind(opt.v)}
                        className={`px-3 h-9 text-xs font-bold ${activePickKind === opt.v ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
                        data-testid={`quotation-picker-kind-${opt.v}`}
                      >{opt.l}</button>
                    ))}
                  </div>
                  <Button variant="outline" size="sm" className="rounded-2xl h-9 ml-auto" onClick={() => setForm((f) => ({ ...f, items: [...f.items, emptyItem()] }))} data-testid="quotation-add-blank-row">
                    <Plus className="h-3.5 w-3.5 mr-1" /> Blank row
                  </Button>
                </div>
                  <div className="flex flex-wrap gap-2">
                    <Select
                      value={form.manufacturer_id || "__all"}
                      onValueChange={(v) => { setForm({ ...form, manufacturer_id: v === "__all" ? "" : v }); setTypeFilter(""); }}
                    >
                      <SelectTrigger className="rounded-2xl h-10 w-[150px]" data-testid="quotation-picker-company-select"><SelectValue placeholder="Company" /></SelectTrigger>
                      <SelectContent className="rounded-2xl">
                        <SelectItem value="__all">All companies</SelectItem>
                        {manufacturers.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Select value={tid || "__all"} onValueChange={(v) => setTypeFilter(v === "__all" ? "" : v)}>
                      <SelectTrigger className="rounded-2xl h-10 w-[150px]" data-testid="quotation-picker-type-select"><SelectValue placeholder="Product type" /></SelectTrigger>
                      <SelectContent className="rounded-2xl">
                        <SelectItem value="__all">All types</SelectItem>
                        {typesForCompany.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {activePickKind === "products" ? (
                      <Select onValueChange={addProduct}>
                        <SelectTrigger className="rounded-2xl h-10 flex-1 min-w-[200px]" data-testid="quotation-add-product"><SelectValue placeholder={filteredProducts.length ? `Pick a product…${oemLabel ? ` (${oemLabel})` : ""}` : "No products match"} /></SelectTrigger>
                        <SelectContent className="rounded-2xl">
                          {filteredProducts.length === 0 ? (
                            <div className="px-2 py-3 text-xs text-muted-foreground text-center">No products{oemLabel ? ` for ${oemLabel}` : " in catalog"}. Pick another company/type or add products under Products admin.</div>
                          ) : (
                            filteredProducts.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}{p.sku ? ` · ${p.sku}` : ""}{p.unit_price ? ` · ${fmtINR(p.unit_price)}` : ""}</SelectItem>)
                          )}
                        </SelectContent>
                      </Select>
                    ) : activePickKind === "training" ? (
                      <Select onValueChange={(v) => addService(v, "training")}>
                        <SelectTrigger className="rounded-2xl h-10 flex-1 min-w-[200px]" data-testid="quotation-add-training"><SelectValue placeholder={filteredTrainings.length ? "Pick a training programme…" : "No training programmes match"} /></SelectTrigger>
                        <SelectContent className="rounded-2xl">
                          {filteredTrainings.length === 0 ? (
                            <div className="px-2 py-3 text-xs text-muted-foreground text-center">No trainings found. Try another company/type or mark items as ‘Training programme’ under Products → Training.</div>
                          ) : (
                            filteredTrainings.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}{s.unit_price ? ` · ${fmtINR(s.unit_price)}` : ""}</SelectItem>)
                          )}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Select onValueChange={(v) => addService(v, "service")}>
                        <SelectTrigger className="rounded-2xl h-10 flex-1 min-w-[200px]" data-testid="quotation-add-service"><SelectValue placeholder={filteredServices.length ? "Pick a technical service…" : "No services match"} /></SelectTrigger>
                        <SelectContent className="rounded-2xl">
                          {filteredServices.length === 0 ? (
                            <div className="px-2 py-3 text-xs text-muted-foreground text-center">No services found. Try another company/type or add under Products → Services.</div>
                          ) : (
                            filteredServices.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}{s.unit_price ? ` · ${fmtINR(s.unit_price)}` : ""}{s.type ? ` · ${s.type}` : ""}</SelectItem>)
                          )}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                </div>
                );
                })()}

                <div className="space-y-3">
                  {form.items.map((it, idx) => {
                    const c = calcLine(it);
                    return (
                      <div key={it._key || idx} className="p-3 rounded-2xl border border-border/60 bg-muted/20" data-testid={`quotation-item-${idx}`}>
                        <div className="flex items-start gap-2 mb-2">
                          <span className="h-6 w-6 grid place-items-center rounded-full bg-primary text-primary-foreground text-xs font-bold shrink-0">{idx + 1}</span>
                          <div className="flex-1 min-w-0">
                            <CatalogNameInput
                              idx={idx}
                              value={it.name}
                              onChange={(v) => updateItem(idx, { name: v })}
                              onPick={(o) => fillItemFromCatalog(idx, o)}
                              options={catalogOptions}
                            />
                            {(it.sku || it.company_name) && (
                              <p className="text-[10px] text-muted-foreground mt-1" data-testid={`quotation-item-meta-${idx}`}>
                                {[it.sku && `Code: ${it.sku}`, it.company_name].filter(Boolean).join(" · ")}
                              </p>
                            )}
                            <Textarea className="rounded-xl text-xs mt-1.5 bg-white" rows={1} value={it.description} onChange={(e) => updateItem(idx, { description: e.target.value })} placeholder="Optional description" />
                          </div>
                          <button type="button" onClick={() => removeItem(idx)} className="h-7 w-7 grid place-items-center rounded-full hover:bg-red-50 text-muted-foreground hover:text-red-600 tap shrink-0" data-testid={`quotation-item-remove-${idx}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
                          <div>
                            <Label className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Qty</Label>
                            <Input type="number" className="rounded-xl h-9 mt-0.5 text-sm bg-white" value={it.quantity} onChange={(e) => updateItem(idx, { quantity: e.target.value })} data-testid={`quotation-item-qty-${idx}`} />
                          </div>
                          <div>
                            <Label className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Unit</Label>
                            <Input className="rounded-xl h-9 mt-0.5 text-sm bg-white" value={it.unit} onChange={(e) => updateItem(idx, { unit: e.target.value })} />
                          </div>
                          <div>
                            <Label className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Rate (₹)</Label>
                            <Input type="number" className="rounded-xl h-9 mt-0.5 text-sm bg-white" value={it.unit_price} onChange={(e) => updateItem(idx, { unit_price: e.target.value })} data-testid={`quotation-item-rate-${idx}`} />
                          </div>
                          <div>
                            <Label className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Amount</Label>
                            <div className="rounded-xl h-9 mt-0.5 px-3 flex items-center justify-end text-sm font-bold bg-muted/40 border border-border/60 tabular-nums" data-testid={`quotation-item-amount-${idx}`}>
                              {fmtINR(c.net)}
                            </div>
                          </div>
                        </div>

                        {/* Accessories — preview the bundle that ships with this line.
                            Snapshotted from the product but editable per-quote in case
                            the salesperson wants to drop or add something for THIS deal. */}
                        {(it.accessories || []).length > 0 && (
                          <div className="mt-2.5 pt-2 border-t border-emerald-200/60" data-testid={`item-accessories-${idx}`}>
                            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-700 mb-1.5">BOM · {(it.accessories || []).length} item{(it.accessories || []).length === 1 ? "" : "s"}</p>
                            <div className="flex flex-wrap gap-1">
                              {(it.accessories || []).map((a, i) => (
                                <span
                                  key={`${a}-${i}`}
                                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-50 border border-emerald-200 text-emerald-800"
                                >
                                  ✓ {a}
                                  <button
                                    type="button"
                                    onClick={() => updateItem(idx, { accessories: it.accessories.filter((_, j) => j !== i) })}
                                    className="ml-0.5 -mr-0.5 h-3.5 w-3.5 grid place-items-center rounded-full hover:bg-red-100 hover:text-red-600 transition"
                                    aria-label="Remove accessory"
                                  >×</button>
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                <div className="p-4 rounded-2xl bg-primary-50/40 border border-primary-200/60 space-y-1.5" data-testid="quotation-totals">
                  <div className="flex justify-between text-sm"><span>Subtotal</span><span className="font-semibold">{fmtINR(totals.subtotal)}</span></div>
                  {totals.lineDiscount > 0 && <div className="flex justify-between text-xs text-muted-foreground"><span>Line discount</span><span>− {fmtINR(totals.lineDiscount)}</span></div>}
                  {totals.overallDiscount > 0 && <div className="flex justify-between text-sm text-primary-700"><span>Discount @ {totals.overallPct}%</span><span className="font-semibold">− {fmtINR(totals.overallDiscount)}</span></div>}
                  <div className="flex justify-between text-sm"><span>GST @ 18%</span><span className="font-semibold">{fmtINR(totals.gst)}</span></div>
                  <div className="flex justify-between text-base pt-2 border-t border-primary-200/60"><strong>Grand total</strong><strong className="text-primary text-lg">{fmtINR(totals.grand)}</strong></div>
                  {editingId ? (
                    <p className="text-[10px] text-muted-foreground pt-1">A discount can be applied on the Review step (negotiation).</p>
                  ) : null}
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-4">
                <div>
                  <Label className="text-xs font-semibold">Validity</Label>
                  <div className="flex flex-wrap gap-2 mt-1.5" data-testid="quotation-validity-options">
                    {[
                      { d: 7,  l: "7 days" },
                      { d: 10, l: "10 days" },
                      { d: 15, l: "15 days" },
                      { d: 30, l: "30 days" },
                      { d: 45, l: "45 days" },
                      { d: 60, l: "60 days" },
                      { d: 90, l: "90 days" },
                    ].map((opt) => (
                      <button
                        type="button"
                        key={opt.d}
                        onClick={() => setForm({ ...form, validity_days: opt.d, valid_until: "" })}
                        data-testid={`quotation-validity-${opt.d}`}
                        className={`px-3 h-9 rounded-2xl border text-xs font-semibold tap transition ${
                          !form.valid_until && form.validity_days === opt.d
                            ? "border-primary bg-primary text-primary-foreground"
                            : "border-border/60 hover:bg-muted/30 text-muted-foreground"
                        }`}
                      >{opt.l}</button>
                    ))}
                    <button
                      type="button"
                      onClick={() => {
                        const d = new Date();
                        d.setDate(d.getDate() + (form.validity_days || 30));
                        setForm({ ...form, valid_until: d.toISOString().slice(0, 10) });
                      }}
                      data-testid="quotation-validity-custom"
                      className={`px-3 h-9 rounded-2xl border text-xs font-semibold tap transition ${
                        form.valid_until
                          ? "border-primary bg-primary text-primary-foreground"
                          : "border-dashed border-border/60 hover:bg-muted/30 text-muted-foreground"
                      }`}
                    >Custom date</button>
                  </div>
                  {form.valid_until && (
                    <div className="mt-2 flex items-center gap-2">
                      <Input
                        type="date"
                        className="rounded-2xl h-11 max-w-[200px]"
                        value={form.valid_until}
                        min={new Date().toISOString().slice(0, 10)}
                        onChange={(e) => setForm({ ...form, valid_until: e.target.value })}
                        data-testid="quotation-valid-until-input"
                      />
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, valid_until: "" })}
                        className="text-[11px] underline text-muted-foreground"
                      >Clear → use days</button>
                    </div>
                  )}
                  <p className="text-[10px] text-muted-foreground mt-1">
                    Shown on the PDF as <strong>"Valid till"</strong>. Quotes auto-expire after this date.
                  </p>
                </div>
                {form.service_category === "services" && (
                  <>
                    <div>
                      <Label className="text-xs font-semibold">Scope of work</Label>
                      <Textarea
                        className="rounded-2xl mt-1.5 font-mono text-xs"
                        rows={10}
                        value={form.scope}
                        onChange={(e) => setForm({ ...form, scope: e.target.value })}
                        placeholder={form.service_subtype === "general" ? "One bullet per line. Indent sub-items with 4 spaces." : "Auto-filled from the master document — edit if needed."}
                        data-testid="quotation-scope"
                      />
                      <p className="text-[10px] text-muted-foreground mt-1">
                        {form.service_subtype === "general"
                          ? "Free-form. Type each scope item on its own line."
                          : "Verbatim text from the master document. Edits here override the default for this quote only."}
                      </p>
                    </div>
                    <div>
                      <Label className="text-xs font-semibold">Client prerequisites</Label>
                      <Textarea
                        className="rounded-2xl mt-1.5 font-mono text-xs"
                        rows={5}
                        value={form.prerequisites}
                        onChange={(e) => setForm({ ...form, prerequisites: e.target.value })}
                        placeholder={form.service_subtype === "general" ? "What the client needs to arrange before the visit." : "Auto-filled from the master document — edit if needed."}
                        data-testid="quotation-prerequisites"
                      />
                    </div>
                  </>
                )}
                <div>
                  <Label className="text-xs font-semibold">Terms &amp; conditions</Label>
                  <Textarea className="rounded-2xl mt-1.5 font-mono text-xs" rows={9} value={form.terms} onChange={(e) => setForm({ ...form, terms: e.target.value })} data-testid="quotation-terms" />
                  <p className="text-[10px] text-muted-foreground mt-1">One bullet per line — auto-formatted on the PDF.</p>
                </div>
                <div>
                  <Label className="text-xs font-semibold">Internal notes (optional)</Label>
                  <Textarea className="rounded-2xl mt-1.5" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                </div>
              </div>
            )}

            {step === 4 && (
              <div className="space-y-5" data-testid="quotation-review">
                <div className="p-4 rounded-2xl bg-muted/40">
                  <p className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground mb-2">Summary</p>
                  <div className="text-sm space-y-1">
                    <p><strong>Client:</strong> {selectedClient?.company_name || "—"}</p>
                    {selectedContact && <p><strong>Contact:</strong> {selectedContact.name}</p>}
                    {form.subject && <p><strong>Subject:</strong> {form.subject}</p>}
                    <p><strong>Items:</strong> {form.items.filter((it) => (it.name || "").trim()).length}</p>
                    <p><strong>Validity:</strong> {form.valid_until ? `Until ${form.valid_until}` : `${form.validity_days} days`}</p>
                    <p><strong>Tax:</strong> GST @ 18%</p>
                  </div>
                </div>

                {/* Manual Authorised-Signatory pick — ADMIN ONLY (15.docx).
                    Non-admins always sign with their own signature. */}
                {isAdmin && (
                <div className="p-4 rounded-2xl border border-border/60">
                  <Label className="text-xs font-semibold">Signatory (on PDF)</Label>
                  <p className="text-[11px] text-muted-foreground mt-0.5 mb-2">Whose name & signature appear under "For RENVIRO SYSTEMS PVT LTD". Defaults to the quotation creator.</p>
                  <Select value={form.signatory_user_id || "__default"} onValueChange={(v) => setForm({ ...form, signatory_user_id: v === "__default" ? "" : v })}>
                    <SelectTrigger className="rounded-2xl h-11" data-testid="quotation-signatory-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="rounded-2xl">
                      <SelectItem value="__default">Quotation creator (default)</SelectItem>
                      {signatories.map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.name}{s.designation ? ` — ${s.designation}` : ""}{s.has_signature ? "" : " (no signature image)"}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                )}

                {/* 38.docx: Discount is available ONLY when an existing quotation
                    is edited/updated (negotiation). First quotation → no discount. */}
                {editingId ? (
                <div className="p-4 rounded-2xl border border-primary-200/60 bg-primary-50/30">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                      <Label className="text-xs font-semibold">Discount (negotiation)</Label>
                      <p className="text-[11px] text-muted-foreground mt-0.5">Applied on the Subtotal, before GST.</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="inline-flex rounded-2xl border border-border/60 overflow-hidden bg-card" data-testid="quotation-discount-mode">
                        <button
                          type="button"
                          onClick={() => setForm({ ...form, _discount_mode: "pct" })}
                          className={`px-3 h-9 text-xs font-bold ${form._discount_mode !== "abs" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
                          data-testid="quotation-discount-mode-pct"
                        >%</button>
                        <button
                          type="button"
                          onClick={() => setForm({ ...form, _discount_mode: "abs" })}
                          className={`px-3 h-9 text-xs font-bold ${form._discount_mode === "abs" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
                          data-testid="quotation-discount-mode-abs"
                        >₹</button>
                      </div>
                      {form._discount_mode === "abs" ? (
                        <>
                          <span className="font-bold text-primary text-lg">₹</span>
                          <Input
                            type="number"
                            min="0"
                            step="100"
                            className="rounded-2xl h-11 w-32 text-right font-bold"
                            value={(() => {
                              const bn = totals.subtotal || 0;
                              const pct = Number(form.overall_discount_pct) || 0;
                              return Math.round(bn * pct / 100);
                            })()}
                            onChange={(e) => {
                              const bn = totals.subtotal || 0;
                              const abs = Math.max(0, Math.min(bn, Number(e.target.value) || 0));
                              const pct = bn > 0 ? (abs / bn) * 100 : 0;
                              setForm({ ...form, overall_discount_pct: Number(pct.toFixed(4)) });
                            }}
                            data-testid="quotation-overall-discount-abs"
                          />
                        </>
                      ) : (
                        <>
                          <Input
                            type="number"
                            min="0"
                            max="100"
                            step="0.5"
                            className="rounded-2xl h-11 w-24 text-right font-bold"
                            value={form.overall_discount_pct}
                            onChange={(e) => setForm({ ...form, overall_discount_pct: e.target.value })}
                            onBlur={(e) => {
                              const raw = Number(e.target.value);
                              if (!Number.isFinite(raw)) { setForm({ ...form, overall_discount_pct: 0 }); return; }
                              const clamped = Math.max(0, Math.min(100, raw));
                              if (clamped !== raw) setForm({ ...form, overall_discount_pct: clamped });
                            }}
                            data-testid="quotation-overall-discount-pct"
                          />
                          <span className="font-bold text-primary text-lg">%</span>
                        </>
                      )}
                    </div>
                  </div>
                  {form._discount_mode !== "abs" && (
                    <>
                      <input
                        type="range"
                        min="0"
                        max="50"
                        step="0.5"
                        value={Number(form.overall_discount_pct) || 0}
                        onChange={(e) => setForm({ ...form, overall_discount_pct: e.target.value })}
                        className="w-full mt-3 accent-primary"
                        data-testid="quotation-overall-discount-slider"
                      />
                      <div className="flex justify-between text-[10px] text-muted-foreground mt-1"><span>0%</span><span>25%</span><span>50%</span></div>
                    </>
                  )}
                  {form._discount_mode === "abs" && totals.subtotal > 0 && (
                    <p className="mt-2 text-[11px] text-muted-foreground">
                      = <strong>{Number(form.overall_discount_pct || 0).toFixed(2)}%</strong> of the Subtotal (₹{Math.round(totals.subtotal).toLocaleString("en-IN")}).
                    </p>
                  )}
                </div>
                ) : null}

                {/* Live breakdown */}
                <div className="p-4 rounded-2xl bg-card border border-border/60 space-y-2" data-testid="quotation-review-breakdown">
                  <div className="flex justify-between text-sm">
                    <span>Subtotal</span>
                    <span className="font-semibold tabular-nums">{fmtINR(totals.subtotal)}</span>
                  </div>
                  {totals.lineDiscount > 0 && (
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>Line-level discount</span>
                      <span className="tabular-nums">− {fmtINR(totals.lineDiscount)}</span>
                    </div>
                  )}
                  {totals.overallDiscount > 0 && (
                    <div className="flex justify-between text-sm text-primary-700">
                      <span>Discount @ {totals.overallPct}%</span>
                      <span className="font-semibold tabular-nums">− {fmtINR(totals.overallDiscount)}</span>
                    </div>
                  )}
                  {totals.overallDiscount > 0 && (
                  <div className="flex justify-between text-sm pt-2 border-t border-border/40">
                    <span>Taxable amount</span>
                    <span className="font-semibold tabular-nums">{fmtINR(totals.taxable)}</span>
                  </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span>GST @ 18%</span>
                    <span className="font-semibold tabular-nums">{fmtINR(totals.gst)}</span>
                  </div>
                  <div className="flex justify-between text-base pt-2 border-t border-primary-200/60">
                    <strong>Grand total</strong>
                    <strong className="text-primary text-xl tabular-nums" data-testid="quotation-review-grand-total">{fmtINR(totals.grand)}</strong>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center justify-between gap-2 pt-5 mt-3 border-t border-border/60 shrink-0">
            <Button variant="ghost" onClick={() => step > 1 ? setStep(step - 1) : onClose()} className="rounded-2xl">
              <ChevronLeft className="h-4 w-4 mr-1" /> {step === 1 ? "Cancel" : "Back"}
            </Button>
            {step < 4 ? (
              <Button onClick={() => setStep(step + 1)} disabled={(step === 1 && !canStep2) || (step === 2 && !canStep3) || (step === 3 && !canStep4)} className="rounded-2xl" data-testid="quotation-next">
                Next <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            ) : (
              <Button onClick={submit} disabled={saving} className="rounded-2xl" data-testid="quotation-create">
                <CheckCircle2 className="h-4 w-4 mr-1" /> {saving ? (editingId ? "Saving…" : "Creating…") : (editingId ? "Save changes" : "Create quotation")}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function QuotationRow({ q, onChanged, onEdit, company }) {
  const navigate = useNavigate();
  const overdue = q.status === "sent" && q.valid_until && new Date(q.valid_until) < new Date();

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/quotations/${q.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(q.number || "quotation").replace(/\//g, "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) { toast.error(fmtError(err)); }
  };

  const setStatus = async (status) => {
    try {
      await api.post(`/quotations/${q.id}/status`, { status });
      toast.success(`Marked ${STATUS_LABEL[status]}`);
      onChanged?.();
    } catch (err) { toast.error(fmtError(err)); }
  };

  const deleteQuote = async () => {
    if (!window.confirm(`Delete quotation ${q.number}? This cannot be undone.`)) return;
    try {
      await api.delete(`/quotations/${q.id}`);
      toast.success(`Deleted ${q.number}`);
      onChanged?.();
    } catch (err) { toast.error(fmtError(err)); }
  };

  const totalText = `${q.currency || "INR"} ${q.grand_total ? new Intl.NumberFormat("en-IN").format(q.grand_total) : "0"}`;
  const validUntilDate = q.valid_until ? fmtDate(q.valid_until) : null;

  return (
    <div className="p-4 rounded-2xl border border-border/60 bg-card hover:bg-muted/20 transition overflow-hidden" data-testid={`quotation-row-${q.id}`}>
      <div
        className="flex items-start gap-3 flex-wrap cursor-pointer"
        onClick={() => navigate(`/quotations/${q.id}`)}
        data-testid={`quotation-open-${q.id}`}
        title="Open quotation page"
      >
        <div className="h-10 w-10 rounded-2xl bg-primary-50 text-primary-700 grid place-items-center shrink-0">
          <FileText className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-mono text-xs font-bold tracking-wider">{q.number}</p>
            <Badge variant="outline" className={`text-[10px] uppercase tracking-wider ${STATUS_COLOR[q.status] || ""}`}>
              {STATUS_LABEL[q.status]}
            </Badge>
            {q.quote_type === "demo" && (
              <Badge variant="outline" className="text-[10px] uppercase tracking-wider bg-amber-50 text-amber-800 border-amber-200" data-testid={`quotation-type-badge-${q.id}`}>Demo</Badge>
            )}
            {q.quote_type === "budgetary" && (
              <Badge variant="outline" className="text-[10px] uppercase tracking-wider bg-sky-50 text-sky-800 border-sky-200" data-testid={`quotation-type-badge-${q.id}`}>Budgetary</Badge>
            )}
            {q.quote_type === "purchase_enquiry" && (
              <Badge variant="outline" className="text-[10px] uppercase tracking-wider bg-indigo-50 text-indigo-700 border-indigo-200" data-testid={`quotation-type-badge-${q.id}`}>Purchase Enquiry</Badge>
            )}
            {q.quote_type === "negotiation" && (
              <Badge variant="outline" className="text-[10px] uppercase tracking-wider bg-violet-50 text-violet-700 border-violet-200" data-testid={`quotation-type-badge-${q.id}`}>Negotiation</Badge>
            )}
            {overdue && <Badge variant="outline" className="text-[10px] uppercase tracking-wider bg-amber-50 text-amber-800 border-amber-200">Validity expired</Badge>}
          </div>
          <p className="text-sm font-semibold truncate mt-0.5">{q.client_company || "—"}{q.subject ? <span className="text-muted-foreground font-normal"> · {q.subject}</span> : null}</p>
          <p className="text-xs text-muted-foreground truncate mt-0.5">
            {fmtINR(q.grand_total)} · valid till {validUntilDate || "—"}{q.contact_name ? ` · ${q.contact_name}` : ""}
          </p>
        </div>
        <div className="flex flex-col items-end shrink-0">
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold whitespace-nowrap">
            {fmtDate(q.created_at)}
          </span>
          {q.owner_name && (
            <span className="text-[10px] text-primary-700 font-semibold whitespace-nowrap mt-0.5" data-testid={`quotation-owner-${q.id}`}>
              Created by: {q.owner_name}
            </span>
          )}
        </div>
      </div>

      <div className="mt-3 flex flex-col lg:flex-row lg:items-center gap-1.5 lg:gap-2">
        <div className="flex flex-wrap items-center gap-1.5">
          <Button variant="outline" size="sm" onClick={downloadPdf} className="rounded-2xl tap text-xs" data-testid={`quotation-pdf-${q.id}`}>
            <Download className="h-3.5 w-3.5 mr-1" /> PDF
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onEdit?.(q)}
            className="rounded-2xl tap text-xs"
            data-testid={`quotation-edit-${q.id}`}
            title="Edit and re-download"
          >
            <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
          </Button>
          {q.quote_type && q.quote_type !== "final" && (
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                if (!window.confirm(`Convert ${q.number} into a Final quotation? This issues a new final-series number.`)) return;
                try {
                  const { data } = await api.post(`/quotations/${q.id}/promote`);
                  toast.success(`Promoted to ${data.number}`);
                  onChanged?.();
                } catch (err) { toast.error(fmtError(err)); }
              }}
              className="rounded-2xl tap text-xs"
              data-testid={`quotation-promote-${q.id}`}
              title="Convert to Final"
            >
              <ArrowRight className="h-3.5 w-3.5 mr-1" /> Make Final
            </Button>
          )}
          {q.client_phone && (
            <WhatsAppButton
              phone={q.client_phone}
              email={q.client_email}
              contactName={q.contact_name || q.client_company}
              clientCompany={q.client_company}
              defaultTemplate="quotation_send"
              quotation={q}
              totalText={totalText}
              validUntil={validUntilDate}
              company={company}
              label="Send"
              testId={`quotation-wa-${q.id}`}
              onSent={async () => { try { await api.post(`/quotations/${q.id}/log-send`, { channel: "whatsapp", to: q.client_phone }); onChanged?.(); } catch {} }}
            />
          )}
          {q.client_phone && <CallButton phone={q.client_phone} testId={`quotation-call-${q.id}`} />}
        </div>
        <div className="flex flex-wrap items-center gap-1.5 lg:ml-auto">
          {/* Manual stage transitions. Sales staff taps these as the real-world
              deal progresses — system can't know when a customer verbally
              accepts/rejects or when validity expires, so everything is
              explicit. A small "Reset" is always available to recover from
              mis-taps without needing admin. */}
          {q.status === "draft" && (
            <Button
              variant="ghost" size="sm"
              onClick={() => setStatus("sent")}
              className="rounded-2xl tap text-xs text-primary-700 hover:bg-primary-50"
              data-testid={`quotation-mark-sent-${q.id}`}
            >
              <ArrowRight className="h-3.5 w-3.5 mr-1" /> Mark sent
            </Button>
          )}
          {q.status === "sent" && (
            <>
              <Button
                variant="ghost" size="sm"
                onClick={() => setStatus("accepted")}
                className="rounded-2xl tap text-xs text-primary-700 hover:bg-primary-50"
                data-testid={`quotation-mark-accepted-${q.id}`}
              >
                <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Accept
              </Button>
              <Button
                variant="ghost" size="sm"
                onClick={() => setStatus("rejected")}
                className="rounded-2xl tap text-xs text-red-700 hover:bg-red-50"
                data-testid={`quotation-mark-rejected-${q.id}`}
              >
                <XCircle className="h-3.5 w-3.5 mr-1" /> Reject
              </Button>
              <Button
                variant="ghost" size="sm"
                onClick={() => setStatus("expired")}
                className="rounded-2xl tap text-xs text-stone-600 hover:bg-stone-100"
                data-testid={`quotation-mark-expired-${q.id}`}
                title="Customer didn't respond in validity window"
              >
                <Clock className="h-3.5 w-3.5 mr-1" /> Expired
              </Button>
            </>
          )}
          {(q.status === "accepted" || q.status === "rejected" || q.status === "expired") && (
            <Button
              variant="ghost" size="sm"
              onClick={() => setStatus("sent")}
              className="rounded-2xl tap text-xs text-muted-foreground hover:bg-muted"
              data-testid={`quotation-reopen-${q.id}`}
              title="Re-open — treat as still under discussion"
            >
              <RotateCcw className="h-3.5 w-3.5 mr-1" /> Re-open
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={deleteQuote}
            className="rounded-2xl tap text-xs text-red-700 hover:bg-red-50"
            data-testid={`quotation-delete-${q.id}`}
            title="Delete quotation"
          >
            <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function Quotations() {
  const [params] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openNew, setOpenNew] = useState(params.get("new") === "1");
  const [editQuote, setEditQuote] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [company, setCompany] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/quotations", { params: { status: statusFilter || undefined } });
      setRows(data);
    } finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/quotations/company").then((r) => setCompany(r.data)); }, []);

  const filtered = rows.filter((q) => {
    if (!search.trim()) return true;
    const needle = search.toLowerCase();
    return (q.number || "").toLowerCase().includes(needle)
      || (q.client_company || "").toLowerCase().includes(needle)
      || (q.subject || "").toLowerCase().includes(needle);
  });

  const byStatus = rows.reduce((acc, q) => { acc[q.status] = (acc[q.status] || 0) + 1; return acc; }, {});
  const totalValue = rows.filter((q) => q.status === "sent" || q.status === "accepted").reduce((s, q) => s + (q.grand_total || 0), 0);

  return (
    <div className="p-4 sm:p-8 lg:p-10 max-w-[1280px] mx-auto">
      <header className="mb-5 sm:mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-muted-foreground">Closing the loop</p>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight mt-1">Quotations.</h1>
          <p className="text-sm text-muted-foreground mt-1.5 max-w-md">
            Draft a professional quotation in 60 seconds. Auto-numbered, GST-ready, beautifully presented PDF — share via WhatsApp or email.
          </p>
        </div>
        <Button onClick={() => setOpenNew(true)} className="rounded-2xl tap" data-testid="new-quotation-button">
          <Plus className="h-4 w-4 mr-1" /> New quotation
        </Button>
      </header>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 sm:gap-4 mb-5">
        {["draft", "sent", "accepted", "rejected", "expired"].map((k) => (
          <button
            key={k}
            onClick={() => setStatusFilter(statusFilter === k ? "" : k)}
            className={`p-2.5 sm:p-4 rounded-2xl border tap text-left transition ${statusFilter === k ? "bg-card border-primary shadow-soft" : "bg-muted/30 border-border/60"}`}
            data-testid={`quotation-tile-${k}`}
          >
            <p className="text-[9px] sm:text-[10px] uppercase tracking-wider font-bold text-muted-foreground whitespace-nowrap">{STATUS_LABEL[k]}</p>
            <p className="font-display text-2xl sm:text-3xl font-black mt-0.5 sm:mt-1.5 leading-none">{byStatus[k] || 0}</p>
          </button>
        ))}
      </div>

      {totalValue > 0 && (
        <div className="mb-5 p-3 rounded-2xl bg-primary-50/40 border border-primary-200/60 flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Active quote value</span>
          <span className="font-display text-lg font-bold text-primary ml-auto">{fmtINR(totalValue)}</span>
        </div>
      )}

      <div className="flex gap-2 sm:gap-3 mb-5">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by number, client or subject…" className="pl-10 h-12 rounded-2xl bg-muted/40" data-testid="quotation-search" />
        </div>
      </div>

      <Card className="rounded-3xl border-border/60 shadow-soft overflow-hidden">
        <CardContent className="p-3 sm:p-4 space-y-2">
          {loading ? (
            <p className="py-12 text-center text-sm text-muted-foreground">Loading…</p>
          ) : filtered.length === 0 ? (
            <div className="py-14 text-center">
              <FileText className="h-7 w-7 text-primary mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">{rows.length === 0 ? "No quotations yet. Draft your first one above." : "No matches for your search."}</p>
            </div>
          ) : (
            filtered.map((q) => <QuotationRow key={q.id} q={q} onChanged={load} onEdit={setEditQuote} company={company} />)
          )}
        </CardContent>
      </Card>

      <NewQuotationWizard
        open={openNew || !!editQuote}
        onClose={() => { setOpenNew(false); setEditQuote(null); }}
        onCreated={() => load()}
        editQuote={editQuote}
        prefilledProductId={params.get("product")}
      />
    </div>
  );
}
